import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { Button } from '@mui/material';
import {
    exportsApi,
    useGetMyPendingExportsQuery,
    downloadExport,
    EXPORT_STATUS,
} from '../../../modules/exports/exports.api';
import Constants from '../../../utils/constants';
import history from '../../../utils/history';

/**
 * Composant sans rendu, monté dans Layout (session ouverte uniquement).
 *
 * Au montage : one-shot sur les exports pending/processing de l'utilisateur
 * (reprend les jobs en vol après un F5). Ensuite : polling toutes les 5s
 * UNIQUEMENT s'il y a des exports à surveiller.
 *
 * Quand un export sort de la liste, on récupère son statut final :
 *   done   → toast succès + bouton Télécharger
 *   failed → toast erreur + lien vers Mes exports
 * Le badge du menu se met à jour via l'invalidation du tag 'Exports'.
 */
function ExportsWatcher() {
    const dispatch = useDispatch();
    const [pollingInterval, setPollingInterval] = useState(0);
    const { data: pending } = useGetMyPendingExportsQuery(undefined, { pollingInterval });
    const previousIdsRef = useRef(null);

    useEffect(() => {
        if (pending === undefined) return;

        const currentIds = new Set(pending.map((e) => e.uuid));

        // Ne poll que s'il reste quelque chose à surveiller.
        setPollingInterval(currentIds.size > 0 ? Constants.EXPORTS_POLLING_MS : 0);

        const previousIds = previousIdsRef.current;
        previousIdsRef.current = currentIds;
        if (previousIds === null) return; // premier passage : rien à comparer

        const finished = [...previousIds].filter((uuid) => !currentIds.has(uuid));
        finished.forEach(async (uuid) => {
            try {
                const result = await dispatch(
                    exportsApi.endpoints.getExport.initiate(uuid, { forceRefetch: true })
                ).unwrap();

                if (result?.statut === EXPORT_STATUS.DONE) {
                    toast.success(
                        <span>
                            Votre export « {result.filename} » est prêt.{' '}
                            <Button size="small" variant="outlined" color="inherit"
                                    onClick={() => downloadExport(uuid)}>
                                Télécharger
                            </Button>
                        </span>,
                        { autoClose: false }
                    );
                    // Rafraîchit la liste + le badge « non consultés ».
                    dispatch(exportsApi.util.invalidateTags(['Exports']));
                } else if (result?.statut === EXPORT_STATUS.FAILED) {
                    toast.error(
                        <span>
                            La génération de « {result.filename} » a échoué.{' '}
                            <Button size="small" variant="outlined" color="inherit"
                                    onClick={() => history.push(Constants.APP_ROUTE_MES_EXPORTS)}>
                                Mes exports
                            </Button>
                        </span>,
                        { autoClose: false }
                    );
                    dispatch(exportsApi.util.invalidateTags(['Exports']));
                }
            } catch (e) {
                // Export supprimé ou inaccessible : rien à notifier.
            }
        });
    }, [pending, dispatch]);

    return null;
}

export default ExportsWatcher;
